// src/pages/tasks/PageMyTasks.jsx
import { useState, useEffect, useMemo } from "react";
import * as api from "../../apiService";
import { TaskCreateModal } from "./TaskCreateModal";
import { TaskDetailModal } from "./TaskDetailModal";
import { useToast } from "../../components/Toast";

const STATUS_CFG = {
  BACKLOG:     { label: "Backlog",     bg: "#f1f5f9", color: "#475569" },
  TO_DO:       { label: "To Do",       bg: "#dbeafe", color: "#1d4ed8" },
  IN_PROGRESS: { label: "In Progress", bg: "#fef3c7", color: "#92400e" },
  REVIEW:      { label: "Review",      bg: "#ede9fe", color: "#5b21b6" },
  DONE:        { label: "Done",        bg: "#d1fae5", color: "#065f46" },
};

const PRIORITY_CFG = {
  LOW:    { label: "Low",    bg: "#f1f5f9", color: "#475569" },
  MEDIUM: { label: "Medium", bg: "#dbeafe", color: "#1d4ed8" },
  HIGH:   { label: "High",   bg: "#fef3c7", color: "#92400e" },
  URGENT: { label: "Urgent", bg: "#fee2e2", color: "#991b1b" },
};

function Badge({ cfg }) {
  return (
    <span style={{
      background: cfg.bg, color: cfg.color,
      border: `1px solid ${cfg.color}33`,
      borderRadius: 4, padding: "2px 8px", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
    }}>
      {cfg.label}
    </span>
  );
}

const STATUS_ORDER = ["BACKLOG", "TO_DO", "IN_PROGRESS", "REVIEW", "DONE"];
const ALL = "ALL";

export function PageMyTasks({ myPerms, userEmail }) {
  const showToast = useToast();
  const [tasks,       setTasks]       = useState([]);
  const [users,       setUsers]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [createModal, setCreateModal] = useState(false);
  const [detailTask,  setDetailTask]  = useState(null);
  const [statusFilter,setStatusFilter]= useState(ALL);
  const [priFilter,   setPriFilter]   = useState(ALL);
  const [search,      setSearch]      = useState("");

  const canManage = myPerms?.includes("TASK_MANAGE");

  useEffect(() => {
    const reqs = [api.fetchTasks()];
    if (canManage) reqs.push(api.fetchUsers());
    Promise.all(reqs)
      .then(([t, u]) => { setTasks(t); if (u) setUsers(u); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [canManage]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return tasks.filter(t => {
      if (statusFilter !== ALL && t.status !== statusFilter) return false;
      if (priFilter    !== ALL && t.priority !== priFilter)  return false;
      if (q && !t.title.toLowerCase().includes(q) &&
               !t.assignedTo.toLowerCase().includes(q) &&
               !(t.department || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [tasks, statusFilter, priFilter, search]);

  async function handleCreate(body) {
    const created = await api.createTask(body);
    setTasks(old => [created, ...old]);
    setCreateModal(false);
    showToast("Task created");
  }

  function handleStatusChange(updated) {
    setTasks(old => old.map(t => t.id === updated.id ? updated : t));
    setDetailTask(updated);
    showToast("Status updated");
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this task?")) return;
    await api.deleteTask(id);
    setTasks(old => old.filter(t => t.id !== id));
    setDetailTask(null);
    showToast("Task deleted", "info");
  }

  const openCount = tasks.filter(t => t.status !== "DONE").length;

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">{canManage ? "All Tasks" : "My Tasks"}</div>
          <div className="page-subtitle">
            {openCount} open task{openCount !== 1 ? "s" : ""}
            {canManage ? " across all users" : " assigned to you"}
          </div>
        </div>
        <div className="page-header-right">
          {canManage && (
            <button className="btn btn-primary" onClick={() => setCreateModal(true)}>
              + New Task
            </button>
          )}
        </div>
      </div>

      <div className="action-bar" style={{ flexWrap: "wrap", gap: 8 }}>
        <input className="form-input" placeholder="Search…" value={search}
          onChange={e => setSearch(e.target.value)} style={{ width: 200 }} />

        <select className="form-select" value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)} style={{ width: 150 }}>
          <option value={ALL}>All Statuses</option>
          {STATUS_ORDER.map(s => (
            <option key={s} value={s}>{STATUS_CFG[s]?.label ?? s}</option>
          ))}
        </select>

        <select className="form-select" value={priFilter}
          onChange={e => setPriFilter(e.target.value)} style={{ width: 140 }}>
          <option value={ALL}>All Priorities</option>
          {["URGENT", "HIGH", "MEDIUM", "LOW"].map(p => (
            <option key={p} value={p}>{PRIORITY_CFG[p]?.label ?? p}</option>
          ))}
        </select>
      </div>

      {loading && <div style={{ color: "#aaa", fontSize: 12 }}>Loading…</div>}
      {error   && <div className="alert-warn">Error: {error}</div>}

      {!loading && !error && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Title</th>
                {canManage && <th>Assigned To</th>}
                <th>Priority</th>
                <th>Department</th>
                <th>Due Date</th>
                <th>Status</th>
                {canManage && <th></th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map(task => {
                const sc  = STATUS_CFG[task.status]     ?? STATUS_CFG.BACKLOG;
                const pc  = PRIORITY_CFG[task.priority] ?? PRIORITY_CFG.MEDIUM;
                const due = task.dueDate && task.status !== "DONE" && new Date(task.dueDate) < new Date();
                return (
                  <tr key={task.id}
                    style={{ cursor: "pointer", background: task.status === "DONE" ? "#f8fafc" : "#fff" }}
                    onClick={() => setDetailTask(task)}>
                    <td className="fw-600" style={{ color: task.status === "DONE" ? "#94a3b8" : "#111827" }}>
                      {task.title}
                    </td>
                    {canManage && <td style={{ fontSize: 12 }}>{task.assignedTo}</td>}
                    <td><Badge cfg={pc} /></td>
                    <td style={{ fontSize: 12, color: "#6b7280" }}>{task.department || "—"}</td>
                    <td style={{ fontSize: 12, whiteSpace: "nowrap",
                      color: due ? "#dc2626" : "#374151", fontWeight: due ? 600 : 400 }}>
                      {task.dueDate || "—"}
                    </td>
                    <td><Badge cfg={sc} /></td>
                    {canManage && (
                      <td onClick={e => e.stopPropagation()}>
                        <button className="btn-small btn-danger"
                          onClick={() => handleDelete(task.id)}>Del</button>
                      </td>
                    )}
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={canManage ? 7 : 5}
                    style={{ textAlign: "center", color: "#aaa", padding: 24 }}>
                    No tasks found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {createModal && (
        <div className="overlay" onClick={e => { if (e.target === e.currentTarget) setCreateModal(false); }}>
          <TaskCreateModal users={users} onSave={handleCreate} onCancel={() => setCreateModal(false)} />
        </div>
      )}

      {detailTask && (
        <div className="overlay" onClick={e => { if (e.target === e.currentTarget) setDetailTask(null); }}>
          <TaskDetailModal
            task={detailTask}
            userEmail={userEmail}
            canManage={canManage}
            onStatusChange={handleStatusChange}
            onClose={() => setDetailTask(null)}
          />
        </div>
      )}
    </>
  );
}
